// WP-3.8 (M20 RE-1/3/4, S-28 `/reports/:key`): parameter bar (period via year-basis toggle,
// currency) + table + a minimal bar chart + Export (CSV/XLSX/PDF) + valuation-basis footnote.
// WP-7.4 (RE-5) adds "zakat" as a SPECIAL-CASED key — never a REPORT_FAMILIES entry, so it
// branches to its own `ZakatView` before ever calling `useReport` (which would 400 on an
// unknown family).
import type { CurrencyCode, ReportFamily, YearBasis } from '@pfos/engine';
import type { ReportResultResponse } from '@pfos/shared';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { ghostButtonInlineClass, selectClass } from '../components/FormKit';
import { downloadCsv, downloadXlsx } from '../lib/export';
import { downloadReportPdf } from '../lib/pdf';
import {
  buildReportExport,
  formatPercent,
  isMoneyColumn,
  isPercentColumn,
  REPORT_COLUMNS,
  REPORT_PRIMARY_COLUMN,
  useReport,
} from '../lib/reports';
import { useSettings } from '../lib/settings';
import { useZakatEstimate } from '../lib/zakat';

const YEAR_BASIS_OPTIONS: { value: YearBasis; label: string }[] = [
  { value: 'calendar', label: 'Jan–Dec' },
  { value: 'fiscal', label: 'Jul–Jun' },
];

function rowLabel(row: ReportResultResponse['rows'][number]): string {
  const first = Object.values(row).find((v) => typeof v === 'string');
  return typeof first === 'string' ? first : '—';
}

function ReportChart({
  report,
  primaryKey,
}: {
  report: ReportResultResponse;
  primaryKey: string;
}): React.JSX.Element | null {
  if (report.rows.length === 0) return null;
  const values = report.rows.map((r) => Number(r[primaryKey] ?? 0));
  const max = Math.max(...values.map(Math.abs), 1);
  return (
    <div className="mb-4 space-y-1">
      {report.rows.map((row, i) => {
        const value = values[i] ?? 0;
        const widthPercent = (Math.abs(value) / max) * 100;
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="w-24 shrink-0 truncate text-xs text-muted">{rowLabel(row)}</span>
            <div className="h-3 flex-1 rounded bg-bg">
              <div
                className={`h-3 rounded ${value < 0 ? 'bg-danger' : 'bg-primary'}`}
                style={{ width: `${String(widthPercent)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** WP-7.4 (RE-5): "informational only" — a standalone qualifying-balance/nisab/rate/due card,
 * never the generic family table. `nisabMinor`/`ratePercent` come from the owner's OWN
 * settings (Financial), never a hardcoded default this app takes a position on. */
function ZakatView(): React.JSX.Element {
  const { data: settings } = useSettings();
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const nisabMinor = typeof settings?.zakatNisabMinor === 'number' ? settings.zakatNisabMinor : 0;
  const ratePercent =
    typeof settings?.zakatRatePercent === 'number' ? settings.zakatRatePercent : 2.5;
  const { data: estimate, isLoading } = useZakatEstimate(currency, nisabMinor, ratePercent);

  return (
    <div>
      <Link
        to="/reports"
        className="mb-2 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={14} /> Reports
      </Link>
      <h1 className="mb-2 text-xl font-semibold text-ink">Zakat</h1>
      <p className="mb-4 text-xs text-muted">
        Informational only, not religious advice (RE-5). Set your own nisab threshold and rate in
        Settings → Financial.
      </p>
      <label className="mb-4 block">
        <span className="mb-1 block text-sm text-muted">Currency</span>
        <select
          className={`${selectClass} w-auto`}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {estimate !== undefined && (
        <div className="max-w-sm space-y-2 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted">Qualifying balance</span>
            <Amount minorUnits={estimate.qualifyingBalanceMinor} currency={currency} />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted">Nisab threshold</span>
            <Amount minorUnits={estimate.nisabMinor} currency={currency} />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted">Meets nisab?</span>
            <span className="text-ink">{estimate.meetsNisab ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-2 text-sm">
            <span className="text-muted">Estimated Zakat due ({estimate.ratePercent}%)</span>
            <Amount
              minorUnits={estimate.zakatDueMinor}
              currency={currency}
              className="font-medium"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyReportView({ family }: { family: ReportFamily }): React.JSX.Element {
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [yearBasis, setYearBasis] = useState<YearBasis | ''>('');
  const { data: report, isLoading } = useReport(family, {
    currency,
    ...(yearBasis !== '' ? { yearBasis } : {}),
  });

  const columns = REPORT_COLUMNS[family];

  return (
    <div>
      <Link
        to="/reports"
        className="mb-2 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={14} /> Reports
      </Link>
      <h1 className="mb-4 text-xl font-semibold capitalize text-ink">
        {family.replace(/([A-Z])/g, ' $1')}
      </h1>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Currency</span>
          <select
            className={`${selectClass} w-auto`}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value as CurrencyCode);
            }}
          >
            {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Year basis (RE-3)</span>
          <select
            className={`${selectClass} w-auto`}
            value={yearBasis}
            onChange={(e) => {
              setYearBasis(e.target.value as YearBasis | '');
            }}
          >
            <option value="">Default (Settings)</option>
            {YEAR_BASIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {report !== undefined && (
          <>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                downloadCsv(`${family}-report.csv`, buildReportExport(report, columns));
              }}
            >
              <Download size={14} className="mr-1 inline" /> CSV
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                downloadXlsx(`${family}-report.xlsx`, family, buildReportExport(report, columns));
              }}
            >
              <Download size={14} className="mr-1 inline" /> XLSX
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                downloadReportPdf(
                  `${family}-report.pdf`,
                  family.replace(/([A-Z])/g, ' $1'),
                  buildReportExport(report, columns),
                );
              }}
            >
              <Download size={14} className="mr-1 inline" /> PDF
            </button>
          </>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {report !== undefined && (
        <>
          <p className="mb-3 text-xs text-muted">
            {new Date(report.period.from).toLocaleDateString()} –{' '}
            {new Date(report.period.to).toLocaleDateString()} ·{' '}
            {report.yearBasis === 'fiscal' ? 'Jul–Jun' : 'Jan–Dec'} · {report.currency} · actual
            amounts, no indicative FX conversion (RE-4)
          </p>

          {report.rows.length === 0 ? (
            <p className="text-sm text-muted">No data for this period.</p>
          ) : (
            <>
              <ReportChart report={report} primaryKey={REPORT_PRIMARY_COLUMN[family]} />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted">
                      {columns.map((c) => (
                        <th key={c.key} className="py-2 pr-4 font-medium">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        {columns.map((c) => (
                          <td key={c.key} className="py-2 pr-4 text-ink">
                            {isMoneyColumn(c.key) && typeof row[c.key] === 'number' ? (
                              <Amount
                                minorUnits={row[c.key] as number}
                                currency={report.currency}
                              />
                            ) : isPercentColumn(c.key) && typeof row[c.key] === 'number' ? (
                              formatPercent(row[c.key] as number)
                            ) : (
                              String(row[c.key] ?? '—')
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function ReportDetailPage(): React.JSX.Element {
  const { key = '' } = useParams<{ key: string }>();
  if (key === 'zakat') return <ZakatView />;
  return <FamilyReportView family={key as ReportFamily} />;
}
